import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as ScheduleActions from './schedule.actions';
import { ScheduleService } from 'app/modules/masters/schedule-master/schedule.service';
import { catchError, map, mergeMap, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { ScheduleState } from './schedule.state';
import {
  selectScheduleSortBy,
  selectScheduleSortOrder,
  selectScheduleListSearch,
  selectScheduleStatusFilter,
} from './schedule.selectors';
import { withLatestFrom } from 'rxjs/operators';

@Injectable()
export class ScheduleEffects {
  constructor(
    private actions$: Actions,
    private service: ScheduleService,
    private store: Store<ScheduleState>,
  ) {}

  loadSchedules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActions.loadSchedules),
      withLatestFrom(
        this.store.select(selectScheduleSortBy),
        this.store.select(selectScheduleSortOrder),
        this.store.select(selectScheduleListSearch),
        this.store.select(selectScheduleStatusFilter),
      ),
      mergeMap(([action, sortBy, sortOrder, listSearch, scheduleStatus]) => {
        const sb = action.sortBy ?? sortBy;
        const so = action.sortOrder ?? sortOrder;
        const search = action.search !== undefined ? action.search : listSearch;
        const st = action.scheduleStatus !== undefined ? action.scheduleStatus : scheduleStatus;
        return this.service.getAll(search, sb, so, st).pipe(
          map((schedules) =>
            ScheduleActions.loadSchedulesSuccess({ schedules, sortBy: sb, sortOrder: so }),
          ),
          catchError((error) => of(ScheduleActions.loadSchedulesFailure({ error }))),
        );
      }),
    ),
  );

  createSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActions.createSchedule),
      mergeMap(({ schedule }) =>
        this.service.create(schedule).pipe(
          map((response: any) => {
            // ✅ Backend returns { message, schedule }, extract schedule object
            const createdSchedule = response.schedule || response;
            return ScheduleActions.createScheduleSuccess({ schedule: createdSchedule });
          }),
          catchError((error) => {
            // ✅ Extract meaningful error message from backend response
            const errorMessage = error?.error?.message || error?.message || 'Failed to create schedule';
            return of(ScheduleActions.createScheduleFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // ✅ Reload schedules after successful create to get fresh list
  reloadAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActions.createScheduleSuccess),
      withLatestFrom(
        this.store.select(selectScheduleSortBy),
        this.store.select(selectScheduleSortOrder),
        this.store.select(selectScheduleListSearch),
        this.store.select(selectScheduleStatusFilter),
      ),
      map(([, sortBy, sortOrder, listSearch, scheduleStatus]) =>
        ScheduleActions.loadSchedules({ sortBy, sortOrder, search: listSearch, scheduleStatus }),
      ),
    ),
  );

  // ✅ Reload schedules after successful update to get fresh list
  reloadAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActions.updateScheduleSuccess),
      withLatestFrom(
        this.store.select(selectScheduleSortBy),
        this.store.select(selectScheduleSortOrder),
        this.store.select(selectScheduleListSearch),
        this.store.select(selectScheduleStatusFilter),
      ),
      map(([action, sortBy, sortOrder, listSearch, scheduleStatus]) => {
        const s = action.schedule as { isDeleted?: boolean; isActive?: boolean };
        const becameInactive =
          s?.isDeleted === true || s?.isActive === false;
        const nextStatus = becameInactive ? 'all' : scheduleStatus;
        return ScheduleActions.loadSchedules({
          sortBy,
          sortOrder,
          search: listSearch,
          scheduleStatus: nextStatus,
        });
      }),
    ),
  );

  updateSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActions.updateSchedule),
      mergeMap(({ id, schedule }) =>
        this.service.update(id, schedule).pipe(
          map((updated) => ScheduleActions.updateScheduleSuccess({ schedule: updated })),
          catchError((error) => {
            // ✅ Extract meaningful error message from backend response
            const errorMessage = error?.error?.message || error?.message || 'Failed to update schedule';
            return of(ScheduleActions.updateScheduleFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  deleteSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActions.deleteSchedule),
      mergeMap(({ id }) =>
        this.service.delete(id).pipe(
          map(() => ScheduleActions.deleteScheduleSuccess({ id })),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Failed to delete schedule';
            return of(ScheduleActions.deleteScheduleFailure({ error: errorMessage }));
          }),
        ),
      ),
    ),
  );

  reloadAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActions.deleteScheduleSuccess),
      withLatestFrom(
        this.store.select(selectScheduleSortBy),
        this.store.select(selectScheduleSortOrder),
        this.store.select(selectScheduleListSearch),
        this.store.select(selectScheduleStatusFilter),
      ),
      map(([, sortBy, sortOrder, listSearch, scheduleStatus]) =>
        ScheduleActions.loadSchedules({
          sortBy,
          sortOrder,
          search: listSearch,
          scheduleStatus: scheduleStatus === 'active' ? 'all' : scheduleStatus,
        }),
      ),
    ),
  );

  checkDuplicateName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScheduleActions.checkScheduleNameDuplicate),
      mergeMap(({ name, excludeId }) =>
        this.service.checkNameDuplicate(name, excludeId).pipe(
          map((res: { exists: boolean }) =>
            ScheduleActions.checkScheduleNameDuplicateSuccess({ exists: res.exists })
          ),
          catchError((error) => of(ScheduleActions.checkScheduleNameDuplicateFailure({ error })))
        )
      )
    )
  );

  // 🔍 Search Schedules
searchSchedules$ = createEffect(() =>
  this.actions$.pipe(
    ofType(ScheduleActions.searchSchedules),
    mergeMap(({ searchTerm }) =>
      this.service.getAll(searchTerm, 'name', 'asc', 'active').pipe(
        map((schedules) =>
          ScheduleActions.searchSchedulesSuccess({
            schedules,
            sortBy: 'name',
            sortOrder: 'asc',
          }),
        ),
        catchError((error) => of(ScheduleActions.searchSchedulesFailure({ error }))),
      ),
    ),
  ),
);

};

